"use client";

import type { FC, PropsWithChildren } from "react";
import React, { useContext } from "react";

import { isEditing } from "@builder.io/sdk-react";

import { useUser } from "@jti/ui/hooks/useUser";

import type { BreadcrumbsList } from "@/app/_internal/getBreadcrumbsList/getBreadcrumbsList";

import HiddenPageRedirectionLayer from "./hidden-page-redirection-layer/HiddenPageRedirectionLayer";

import type { PageConfig } from "./schema";
import { DEFAULT_PAGE_CONFIG_VALUE } from "./schema";

export type PageConfigContext = PageConfig & {
  breadcrumbsList: BreadcrumbsList;
  useHeadingTagForBreadcrumbs?: boolean;
  transparentHeaderTheme?: "light" | "dark" | "inherit";
};

const PageConfigContext = React.createContext<PageConfigContext>({
  ...DEFAULT_PAGE_CONFIG_VALUE,
  breadcrumbsList: [],
});

export const PageConfigProvider: FC<PropsWithChildren<{ value: PageConfigContext }>> = ({ children, value }) => {
  const { isUserLoggedIn } = useUser();

  const isRedirectionLayerNeeded = value.isPageShouldBeProtected && !isUserLoggedIn && !isEditing();

  return (
    <PageConfigContext.Provider value={value}>
      {
        // This is temporary workaround as was requested by HU market, content gate should be used instead for this kind of operations DESP-107407
      }
      {isRedirectionLayerNeeded ? (
        <HiddenPageRedirectionLayer
          userShouldBeAgeVerified={false}
          userShouldDeviceBeRegistered={false}
          redirectPageForNotAllowedUsers={""}
          redirectToAuthPage={false}
          requiredConsentPurposes={[]}
          redirectTime={3000}
          isInEditor={isEditing()}
        />
      ) : (
        children
      )}
    </PageConfigContext.Provider>
  );
};

export const usePageConfig = (): PageConfigContext => {
  return useContext(PageConfigContext);
};
